import { ChangeEvent, FC, Fragment, useMemo, useState } from 'react';

import { DOT } from 'utils/constants/common';

import multicreationSelectAllButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_select_all_button_click';
import {
    ActionBar,
    BottomSheet,
    Button,
    Cell,
    Checkbox,
    HSpacing,
    Loader,
    Modal,
    NavBar,
    Text,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import { AddVacanciesModalTableRow } from 'components/MultiCreationAuction/AddVacanciesModal/AddVacanciesModalTableRow/AddVacanciesModalTableRow';
import { fetchVacanciesAuctionInfo } from 'components/MultiCreationAuction/api/fetchVacanciesAuctionInfo';
import { SearchNotFound } from 'components/MultiCreationAuction/SearchNotFound/SearchNotFound';
import { VacanciesSearchFilter } from 'components/MultiCreationAuction/VacanciesFiltersPanel/VacancySearchFilter/VacancySearchFilter';
import { MAX_VACANCY_SELECT } from 'components/MultiCreationAuction/VacanciesTable/VacanciesTable';

import {
    selectMultiCreationVacanciesAuctionInfo,
    setMultiCreationVacanciesSearchValue,
} from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/AddVacanciesModal/AddVacanciesModal.module.less';

const TrlKeys = {
    title: 'MultiCreationAuction.AddVacanciesModal.title',
    addButton: 'MultiCreationAuction.AddVacanciesModal.addButton',
    cancelButton: 'MultiCreationAuction.AddVacanciesModal.cancelButton',
    selectAll: 'MultiCreationAuction.VacanciesTable.VacanciesTableHeader.selectAll',
};

interface AddVacanciesModalProps {
    isVisible: boolean;
    closeModal: () => void;
}

export const AddVacanciesModal: FC<AddVacanciesModalProps> = ({ isVisible, closeModal }) => {
    const vacancies = useSelector(({ multiCreation }) => multiCreation.vacancies);
    const vacanciesAuctionInfo = useSelector(selectMultiCreationVacanciesAuctionInfo);
    const vacanciesIsLoading = useSelector(({ multiCreation }) => multiCreation.vacanciesIsLoading);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedVacancyIds, setSelectedVacancyIds] = useState<Record<number, boolean>>({});

    const trls = useTrls();
    const dispatch = useDispatch();

    const selectedVacanciesCount = Object.keys(selectedVacancyIds).length;

    const availableToSelect = useMemo(
        () =>
            vacancies.reduce(
                (totalCount, vacancy) => (vacanciesAuctionInfo[vacancy.vacancyId] ? totalCount : totalCount + 1),
                0
            ),
        [vacancies, vacanciesAuctionInfo]
    );

    const isSelectAllDisabled =
        Object.keys(vacanciesAuctionInfo).length >= MAX_VACANCY_SELECT ||
        vacancies.every(({ vacancyId }) => vacanciesAuctionInfo[vacancyId]);

    const handleCloseModal = () => {
        dispatch(setMultiCreationVacanciesSearchValue(''));
        closeModal();
        setSelectedVacancyIds({});
    };

    const handleAddVacancies = () => {
        dispatch(setMultiCreationVacanciesSearchValue(''));
        void dispatch(fetchVacanciesAuctionInfo(Object.keys(selectedVacancyIds), setIsLoading)).then(handleCloseModal);
    };

    const handleSelectAllToggle = (event: ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;

        multicreationSelectAllButtonClick({
            place: 'modal',
            checkedVacanciesCount: selectedVacanciesCount,
        });

        if (!checked) {
            setSelectedVacancyIds({});
            return;
        }

        const newSelectedVacanciesIds = { ...selectedVacancyIds };
        vacancies.some(({ vacancyId }) => {
            if (!vacanciesAuctionInfo[vacancyId]) {
                newSelectedVacanciesIds[vacancyId] = true;
            }
            return Object.keys(newSelectedVacanciesIds).length >= MAX_VACANCY_SELECT;
        });
        setSelectedVacancyIds(newSelectedVacanciesIds);
    };

    const content = (
        <div className={styles.wrapper}>
            {vacanciesIsLoading && vacancies.length === 0 && (
                <div className={styles.loaderWrapper}>
                    <Loader size={24} />
                </div>
            )}
            {!vacanciesIsLoading && vacancies.length === 0 && <SearchNotFound />}
            {vacancies.length > 0 && (
                <>
                    <Cell
                        left={
                            <Checkbox
                                disabled={isSelectAllDisabled}
                                checked={selectedVacanciesCount > 0}
                                indeterminate={selectedVacanciesCount > 0 && selectedVacanciesCount < vacancies.length}
                                onChange={handleSelectAllToggle}
                            />
                        }
                    >
                        <div className={isSelectAllDisabled ? styles.opacity50 : ''}>
                            <Text typography="label-2-regular">
                                {trls(TrlKeys.selectAll)}
                                <HSpacing default={4} />
                                {availableToSelect > 0 && (
                                    <>
                                        {DOT}
                                        <HSpacing default={4} />
                                        {availableToSelect}
                                    </>
                                )}
                            </Text>
                        </div>
                    </Cell>
                    <VSpacing default={24} />
                    {vacancies.map((vacancy) => (
                        <Fragment key={vacancy.vacancyId}>
                            <AddVacanciesModalTableRow
                                vacancy={vacancy}
                                selectedVacancyIds={selectedVacancyIds}
                                setSelectedVacancyIds={setSelectedVacancyIds}
                            />
                            <VSpacing default={16} />
                        </Fragment>
                    ))}
                </>
            )}
        </div>
    );

    const footer = (
        <ActionBar
            primaryActions={
                <>
                    <Button
                        data-qa="add-vacancies-modal-cancel"
                        mode="secondary"
                        style="accent"
                        onClick={handleCloseModal}
                    >
                        {trls(TrlKeys.cancelButton)}
                    </Button>
                    <Button
                        data-qa="add-vacancies-modal-add"
                        loading={isLoading}
                        mode="primary"
                        style="accent"
                        onClick={handleAddVacancies}
                        disabled={selectedVacanciesCount === 0}
                    >
                        {trls(TrlKeys.addButton)}
                        <HSpacing default={8} />
                        {selectedVacanciesCount}
                    </Button>
                </>
            }
        />
    );

    return (
        <>
            <Modal
                header={
                    <MagritteModalHeader
                        title={trls(TrlKeys.title)}
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={handleCloseModal} />}
                        options={<VacanciesSearchFilter />}
                    />
                }
                footer={footer}
                visible={isVisible}
                onClose={handleCloseModal}
                data-qa="add-vacancies-modal"
            >
                {content}
            </Modal>
            <BottomSheet
                height="full-screen"
                visible={isVisible}
                onClose={handleCloseModal}
                header={
                    <NavBar>
                        <NavBar.Pane>
                            <NavBar.Actions
                                right={
                                    <CrossOutlinedSize24
                                        padding={8}
                                        initialColor="secondary"
                                        onClick={handleCloseModal}
                                    />
                                }
                            >
                                <NavBar.TitleContainer
                                    title={<Text typography="title-4-semibold">{trls(TrlKeys.title)}</Text>}
                                />
                            </NavBar.Actions>
                            <VSpacing default={16} />
                            <VacanciesSearchFilter />
                            <VSpacing default={16} />
                        </NavBar.Pane>
                    </NavBar>
                }
                footer={footer}
                data-qa="auction-editing-bottom-sheet"
            >
                {content}
            </BottomSheet>
        </>
    );
};
